import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixCalendarV3Event from './calendar-v3-event-events.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * An event is a scheduled entry on a calendar that includes details like timing,
 * location, and participants. Each event is associated with
 * a [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),
 * from which it may [inherit default values](https://dev.wix.com/docs/rest/business-management/calendar/default-values).
 * Events can either be standalone, part of a recurring series, or define a
 * recurrence pattern. You can also specify whether events block time on the
 * schedule or allow other events to be scheduled concurrently.
 */
export interface Event {
  /**
   * Event ID.
   * @minLength 36
   * @maxLength 250
   * @readonly
   */
  _id?: string | null;
  /**
   * ID of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction)
   * the event belongs to.
   * After creating an event, you can't assign it to a different schedule.
   * @format GUID
   * @immutable
   */
  scheduleId?: string | null;
  /**
   * ID of the external schedule the event belongs to.
   *
   * For example, if the event belongs to a Bookings staff member, identical to the ID of the [resource](https://dev.wix.com/docs/sdk/backend-modules/bookings/resources/introduction). If the schedule belongs to a Bookings service, identical to the ID of the [service](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/setup).
   * @format GUID
   * @readonly
   */
  externalScheduleId?: string | null;
  /**
   * Schedule name.
   * @readonly
   */
  scheduleName?: string | null;
  /**
   * Event type. You can set the event type but you can't update it.
   *
   * Supported values:
   * + `DEFAULT`: A standard event that's not further specified.
   * + `WORKING_HOURS`: The event adds working hours to a schedule. By default not returned in [queryEvents()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/query-events).
   *
   * Additional supported values, if Wix Bookings is installed:
   * + `APPOINTMENT`: Bookings appointment event.
   * + `CLASS`: Bookings class event.
   * + `COURSE`: Bookings course event.
   * @immutable
   */
  type?: string | null;
  /**
   * Event status.
   *
   * Supported values:
   * + `CONFIRMED`: Event is scheduled to happen or has happened.
   * + `CANCELLED`: Event has been canceled.
   *
   * Default: `CONFIRMED`
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Event title.
   *
   * Min: 1 character
   * Max: 200 characters
   * @minLength 1
   * @maxLength 200
   */
  title?: string | null;
  /** Information about when the event starts. */
  start?: ZonedDate;
  /**
   * Information about when the event ends.
   *
   * Maximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first).
   */
  end?: ZonedDate;
  /**
   * Information about when the event starts adjusted to the `timeZone` of the
   * business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties)
   * or a different `timeZone` you provide in the call's request.
   * @readonly
   */
  adjustedStart?: ZonedDate;
  /**
   * Information about when the event ends adjusted to the `timeZone` of the
   * business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties)
   * or a different `timeZone` you provide in the call's request.
   * @readonly
   */
  adjustedEnd?: ZonedDate;
  /**
   * Time zone the event is associated with in
   * [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * Only regional time zones and UTC are supported.
   * For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction).
   */
  timeZone?: string | null;
  /**
   * Information about whether the event is a single occurrence or part of a
   * recurring series.
   *
   * Supported values:
   * + `NONE`: The event occurs only once and doesn't repeat.
   * + `MASTER`: Defines the recurrence pattern for a series of recurring events.
   * + `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{"recurrenceType": "INSTANCE"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.
   * + `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.
   *
   * Default: `NONE`
   * @readonly
   */
  recurrenceType?: RecurrenceTypeWithLiterals;
  /**
   * Recurrence pattern for a series of events. This field is required when
   * creating a `MASTER` event and isn't available for non-recurring events.
   * You can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.
   *
   * For example, an event that repeats every second Monday until January 7, 2026,
   * at 8:00 AM has the following `recurrenceRule`:
   * - `frequency = WEEKLY`
   * - `interval = 2`
   * - `days = [MONDAY]`
   * - `until = 20260107T08:00:00Z`
   */
  recurrenceRule?: RecurrenceRule;
  /**
   * ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`
   * and `EXCEPTION` events.
   * @minLength 64
   * @maxLength 64
   * @readonly
   */
  recurringEventId?: string | null;
  /**
   * Specifies whether the event blocks time in the schedule it belongs to.
   *
   * Supported values:
   * + `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.
   * + `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.
   *
   * Default: `OPAQUE`
   */
  transparency?: TransparencyWithLiterals;
  /**
   * Event location.
   *
   * Default: `defaultLocation` of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction) the event belongs to
   */
  location?: Location;
  /**
   * List of [Wix Bookings resources](https://dev.wix.com/docs/sdk/backend-modules/bookings/resources/introduction)
   * affected by the event. This could include, for example, the
   * [Wix Bookings staff member](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/introduction)
   * providing the service or the room where the appointment takes place.
   *
   * Max: 100 resources
   * @maxSize 100
   */
  resources?: Resource[];
  /**
   * Maximum number of participants who can participate in the event.
   *
   * Default: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction) the event belongs to.
   */
  totalCapacity?: number | null;
  /**
   * Number of participants who can still be added to the event.
   * @readonly
   */
  remainingCapacity?: number | null;
  /**
   * Information about the event's participants.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions#retrieve-personal-information).
   * @readonly
   */
  participants?: Participants;
  /**
   * Information about the event's online conferencing.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions#retrieve-personal-information).
   *
   * Default: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction) the event belongs to.
   */
  conferencingDetails?: ConferencingDetails;
  /**
   * Additional notes about the event.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions#retrieve-personal-information).
   *
   * Min: 1 character
   * Max: 5000 characters
   * @minLength 1
   * @maxLength 5000
   */
  notes?: string | null;
  /**
   * List of fields whose values are inherited.
   * + For single-occurrence events, values are inherited from the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction).
   * + For `INSTANCE` and `EXCEPTION` events, values are inherited from the related `MASTER` event.
   * @readonly
   */
  inheritedFields?: FieldWithLiterals[];
  /**
   * ID of the app that owns the event. Identical to `appId` of the
   * [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction)
   * to which the event belongs.
   * @format GUID
   * @readonly
   */
  appId?: string | null;
  /**
   * List of permissions associated with the event. Refer to the
   * [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions#roles)
   * for more information.
   * @readonly
   */
  permissions?: Permission[];
  /** Extensions enabling applications or users to save custom data related to the event. */
  extendedFields?: ExtendedFields;
  /**
   * Revision number, which increments each time the event is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the event.
   * Ignored when creating an event.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export enum Status {
  /** Event is scheduled to happen or has happened. */
  CONFIRMED = 'CONFIRMED',
  /** Event has been canceled. */
  CANCELLED = 'CANCELLED',
}

/** @enumType */
export type StatusWithLiterals = Status | 'CONFIRMED' | 'CANCELLED';

/** A date time with a time zone, having the UTC offset and date determined by the server. */
export interface ZonedDate {
  /**
   * Local date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * For example, `2024-01-30T13:30:00`.
   * Wix Calendar APIs ignore seconds.
   * @format LOCAL_DATE_TIME
   */
  localDate?: string | null;
  /**
   * Time zone in
   * [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * For example, `America/New_York`.
   * @readonly
   */
  timeZone?: string | null;
  /**
   * UTC date time in [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * For example, `2024-01-30T13:30:00`.
   * Not available for adjusted date fields.
   * @readonly
   */
  utcDate?: Date | null;
}

export enum RecurrenceType {
  /** The event occurs only once and doesn't repeat. */
  NONE = 'NONE',
  /** Defines the recurrence pattern for a series of recurring events. */
  MASTER = 'MASTER',
  /** A specific occurrence of a recurring event. You can't create an event with `{"recurrenceType": "INSTANCE"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`. */
  INSTANCE = 'INSTANCE',
  /** A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event. */
  EXCEPTION = 'EXCEPTION',
}

/** @enumType */
export type RecurrenceTypeWithLiterals =
  | RecurrenceType
  | 'NONE'
  | 'MASTER'
  | 'INSTANCE'
  | 'EXCEPTION';

export interface RecurrenceRule {
  /**
   * Frequency how often the event repeats. Works together with `interval`.
   *
   * Supported values:
   * + `WEEKLY`: The event's recurrence pattern is based on weeks.
   */
  frequency?: FrequencyWithLiterals;
  /**
   * Interval how often the event repeats. Works together with `frequency`.
   * For example, `frequency` set to `WEEKLY`  and `interval` set to `2` means
   * the event repeats every 2 weeks.
   *
   * Min: `1`
   * Max: `4`
   * Default: `1`
   * @min 1
   * @max 4
   */
  interval?: number | null;
  /**
   * Days of the week when the recurring event takes place.
   * Currently, only a single day is supported.
   *
   * Min: 1 day
   * Max: 1 day
   * @minSize 1
   * @maxSize 1
   */
  days?: DayWithLiterals[];
  /**
   * Date until when the event repeats.
   * If not specified, the event repeats forever.
   */
  until?: ZonedDate;
  /**
   * Date until when the event repeats
   * adjusted to the `timeZone` of the business
   * [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties)
   * or a different `timeZone` you provide in the call's request.
   * @readonly
   */
  adjustedUntil?: ZonedDate;
}

export enum Frequency {
  UNKNOWN_FREQUENCY = 'UNKNOWN_FREQUENCY',
  /** The event's recurrence pattern is based on weeks. */
  WEEKLY = 'WEEKLY',
}

/** @enumType */
export type FrequencyWithLiterals = Frequency | 'UNKNOWN_FREQUENCY' | 'WEEKLY';

export enum Day {
  /** The recurring event takes place on Mondays. */
  MONDAY = 'MONDAY',
  /** The recurring event takes place on Tuesdays. */
  TUESDAY = 'TUESDAY',
  /** The recurring event takes place on Wednesday. */
  WEDNESDAY = 'WEDNESDAY',
  /** The recurring event takes place on Thursdays. */
  THURSDAY = 'THURSDAY',
  /** The recurring event takes place on Fridays. */
  FRIDAY = 'FRIDAY',
  /** The recurring event takes place on Saturdays. */
  SATURDAY = 'SATURDAY',
  /** The recurring event takes place on Sundays. */
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayWithLiterals =
  | Day
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export enum Transparency {
  UNKNOWN_TRANSPARENCY = 'UNKNOWN_TRANSPARENCY',
  /** The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time. */
  OPAQUE = 'OPAQUE',
  /** The schedule remains open during the event, allowing other events to be scheduled concurrently. */
  TRANSPARENT = 'TRANSPARENT',
}

/** @enumType */
export type TransparencyWithLiterals =
  | Transparency
  | 'UNKNOWN_TRANSPARENCY'
  | 'OPAQUE'
  | 'TRANSPARENT';

export interface Location {
  /**
   * [Location](https://dev.wix.com/docs/rest/business-management/locations/location-object)
   * ID. Available only for `BUSINESS` locations.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Location type.
   *
   * Supported values:
   * + `BUSINESS`: The event is held at a business location.
   * + `CUSTOMER`: The event is held at the customer's location, such as their home or office.
   * + `CUSTOM`: The event is held at an address or venue not tied to the business or customer.
   */
  type?: LocationTypeWithLiterals;
  /**
   * Location name. For `BUSINESS` locations, it's identical to the
   * [location](https://dev.wix.com/docs/rest/business-management/locations/location-object)
   * `name`.
   *
   * Min: 1 character
   * Max: 150 characters
   * @minLength 1
   * @maxLength 150
   */
  name?: string | null;
  /**
   * Location address. For `BUSINESS` locations, it's identical to the
   * [location](https://dev.wix.com/docs/rest/business-management/locations/location-object)'s
   * `formattedAddress`.
   * @minLength 1
   * @maxLength 300
   */
  address?: string | null;
}

export enum LocationType {
  /** The event is held at a business location. */
  BUSINESS = 'BUSINESS',
  /** The event is held at the customer's location, such as their home or office. */
  CUSTOMER = 'CUSTOMER',
  /** The event is held at an address or venue not tied to the business or customer. */
  CUSTOM = 'CUSTOM',
}

/** @enumType */
export type LocationTypeWithLiterals =
  | LocationType
  | 'BUSINESS'
  | 'CUSTOMER'
  | 'CUSTOM';

export interface Resource {
  /**
   * Resource ID.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Resource name.
   * @readonly
   */
  name?: string | null;
  /**
   * Resource type.
   * @readonly
   */
  type?: string | null;
  /**
   * Specifies whether the event blocks time in the resource's schedule.
   *
   * Supported values:
   * + `OPAQUE`: The schedule is blocked during the event, preventing other events that involve the same resource from being scheduled at the same time.
   * + `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.
   *
   * Default: `OPAQUE`.
   */
  transparency?: TransparencyWithLiterals;
  /**
   * Permission role associated with the resource. Refer to the
   * [permissions article](https://dev.wix.com/docs/rest/business-management/calendar/permissions#roles)
   * for more information.
   */
  permissionRole?: RoleWithLiterals;
}

export enum Role {
  /** Full read and write access. */
  WRITER = 'WRITER',
  /** Full read access, write access limited to `event.notes`. */
  COMMENTER = 'COMMENTER',
}

/** @enumType */
export type RoleWithLiterals = Role | 'WRITER' | 'COMMENTER';

export interface Participants {
  /**
   * Total number of participants.
   *
   * Min: `0`
   */
  total?: number | null;
  /**
   * Full or partial list of the participants.
   *
   * Max: 50 participants
   * @maxSize 50
   */
  list?: Participant[];
  /**
   * Whether there are more participants for the event than listed.
   *
   * + `false`: The list includes all participants.
   * + `true`: The list doesn't include all participants.
   */
  hasMore?: boolean | null;
  /**
   * Participant's status.
   *
   * Supported values:
   * + `CONFIRMED`: All participants are confirmed.
   * + `PENDING_CONFIRMATION`: At least one participant isn't confirmed yet.
   */
  status?: ParticipantsStatusWithLiterals;
}

export interface Participant {
  /**
   * Participant name.
   *
   * Min: 1 character
   * Max: 200 characters
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /**
   * Participant's phone number.
   * @format PHONE
   */
  phone?: string | null;
  /**
   * Participant's email address.
   * @format EMAIL
   */
  email?: string | null;
  /**
   * [Contact](https://dev.wix.com/docs/sdk/backend-modules/crm/contacts/introduction)
   * ID of the participant.
   * @format GUID
   */
  contactId?: string | null;
}

export enum ParticipantsStatus {
  /** All participants are confirmed. */
  CONFIRMED = 'CONFIRMED',
  /** At least one participant isn't confirmed yet. */
  PENDING_CONFIRMATION = 'PENDING_CONFIRMATION',
}

/** @enumType */
export type ParticipantsStatusWithLiterals =
  | ParticipantsStatus
  | 'CONFIRMED'
  | 'PENDING_CONFIRMATION';

export interface ConferencingDetails {
  /** Conference type. For example, `Zoom`. */
  type?: TypeWithLiterals;
  /**
   * URL used by the host to start the conference.
   *
   * Min: 1 character
   * Max: 2000 characters
   * @minLength 1
   * @maxLength 2000
   */
  hostUrl?: string | null;
  /**
   * URL used by a guest to join the conference.
   *
   * Min: 1 character
   * Max: 2000 characters
   * @minLength 1
   * @maxLength 2000
   */
  guestUrl?: string | null;
  /**
   * Conference password.
   *
   * Min: 1 character
   * Max: 100 characters
   * @minLength 1
   * @maxLength 100
   */
  password?: string | null;
  /**
   * Conference ID in an external system.
   *
   * Min: 1 character
   * Max: 150 characters
   * @minLength 1
   * @maxLength 150
   */
  externalId?: string | null;
  /**
   * Conferencing provider information.
   * @internal
   */
  provider?: Provider;
}

export enum ProviderType {
  /** Custom conferencing link manually configured by the user. */
  CUSTOM = 'CUSTOM',
  /** Online meeting provider integrated via an app. */
  ONLINE_MEETING = 'ONLINE_MEETING',
}

/** @enumType */
export type ProviderTypeWithLiterals =
  | ProviderType
  | 'CUSTOM'
  | 'ONLINE_MEETING';

export interface OnlineMeetingInfo {
  /** @format GUID */
  appId?: string;
}

export enum Type {
  UNKNOWN_TYPE = 'UNKNOWN_TYPE',
  ZOOM = 'ZOOM',
  CUSTOM = 'CUSTOM',
}

/** @enumType */
export type TypeWithLiterals = Type | 'UNKNOWN_TYPE' | 'ZOOM' | 'CUSTOM';

export interface Provider extends ProviderProviderInfoOneOf {
  /** Information about online meeting providers. */
  onlineMeetingInfo?: OnlineMeetingInfo;
  providerType?: ProviderTypeWithLiterals;
}

/** @oneof */
export interface ProviderProviderInfoOneOf {
  /** Information about online meeting providers. */
  onlineMeetingInfo?: OnlineMeetingInfo;
}

export enum Field {
  /** `title` is inherited from the schedule or `MASTER` event. */
  TITLE = 'TITLE',
  /** `timeZone` is inherited from the schedule or `MASTER` event. */
  TIME_ZONE = 'TIME_ZONE',
  /** `start` and `end` are inherited from the `MASTER` event. */
  TIME = 'TIME',
  /** `location` is inherited from the schedule or `MASTER` event. */
  LOCATION = 'LOCATION',
  /** `resources` is inherited from the `MASTER` event. */
  RESOURCES = 'RESOURCES',
  /** `capacity` is inherited from the schedule or `MASTER` event. */
  CAPACITY = 'CAPACITY',
  /** `participants` is inherited from the `MASTER` event. */
  PARTICIPANTS = 'PARTICIPANTS',
  /** `conferencingDetails` is inherited from the schedule or `MASTER` event. */
  CONFERENCING_DETAILS = 'CONFERENCING_DETAILS',
}

/** @enumType */
export type FieldWithLiterals =
  | Field
  | 'TITLE'
  | 'TIME_ZONE'
  | 'TIME'
  | 'LOCATION'
  | 'RESOURCES'
  | 'CAPACITY'
  | 'PARTICIPANTS'
  | 'CONFERENCING_DETAILS';

export interface Permission {
  /**
   * [Wix user](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities#wix-users)
   * granted the permission.
   */
  identity?: CommonIdentificationData;
  /**
   * Permission role.
   *
   * Supported values:
   * + `WRITER`: Full read and write access.
   * + `COMMENTER`: Full read access, write access limited to `event.notes`.
   */
  role?: RoleWithLiterals;
}

export interface CommonIdentificationData
  extends CommonIdentificationDataIdOneOf {
  /**
   * ID of a Wix user. For example, the site owner or a
   * [site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).
   * @format GUID
   */
  wixUserId?: string;
}

/** @oneof */
export interface CommonIdentificationDataIdOneOf {
  /**
   * ID of a Wix user. For example, the site owner or a
   * [site collaborator](https://support.wix.com/en/article/inviting-people-to-contribute-to-your-site).
   * @format GUID
   */
  wixUserId?: string;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  WIX_USER = 'WIX_USER',
}

/** @enumType */
export type IdentityTypeWithLiterals = IdentityType | 'UNKNOWN' | 'WIX_USER';

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface RecurringEventSplit {
  /** Original `MASTER` event that was shortened. */
  updatedRecurringEventEndingBeforeSplit?: Event;
  /** New `MASTER` event starting with the first event after the split date. */
  newRecurringEventStartingFromSplit?: Event;
}

export interface EventCancelled {
  /** Canceled event. */
  event?: Event;
}

export interface ParticipantNotification {
  /**
   * Whether to notify the participants about changes made to the schedule or event.
   *
   * Default: `false`
   */
  notifyParticipants?: boolean | null;
  /**
   * Message to send.
   *
   * Min: 1 character
   * Max: 5000 characters
   * @minLength 1
   * @maxLength 5000
   */
  message?: string | null;
  /**
   * Information about the delivery channels used to send the notification.
   * For example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.
   *
   * Max: 10 keys
   * Max key length: 64 characters
   * Max value length: 1000 characters
   */
  metadata?: Record<string, string>;
}

/** Deprecated. Use EventUpdated instead, which includes the modified fields and additional metadata. */
export interface EventUpdatedWithMetadata {
  /** The updated event. */
  event?: Event;
  /**
   * Information about whether participants of the updated event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
}

export interface UpdateScheduleWithFixedBusinessResourceIdRequest {
  /** @format GUID */
  metaSiteId?: string;
  /** @format GUID */
  currentBusinessResourceId?: string;
  migrateBusinessConferenceId?: boolean;
}

export interface UpdateScheduleWithFixedBusinessResourceIdResponse {}

export interface UpdateEventsWithFixedBusinessResourceIdRequest {
  /** @format GUID */
  metaSiteId?: string;
  /** @format GUID */
  currentBusinessResourceId?: string;
  migrateBusinessConferenceId?: boolean;
}

export interface UpdateEventsWithFixedBusinessResourceIdResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface ListEventsByContactIdRequest {
  /**
   * ID of the [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)
   * to retrieve events for. Required, unless you provide `cursorPaging`.
   * @format GUID
   */
  contactId: string | null;
  /**
   * Local start date and time from which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`. Required if `cursorPaging` isn't provided.
   *
   * Events that start before the `fromLocalDate` but end after it are included in
   * the results. Must be earlier than `toLocalDate` unless the sort order is descending.
   * @format LOCAL_DATE_TIME
   */
  fromLocalDate?: string | null;
  /**
   * Local end date and time up to which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`. Can't be more than a full year after
   * `2024-01-30T13:30:00`. Required if `cursorPaging` isn't provided.
   *
   * Events that start before `toLocalDate` but end after it are included in the
   * results. Must be later than `fromLocalDate` unless the sort order is descending.
   * @format LOCAL_DATE_TIME
   */
  toLocalDate?: string | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and
   * `adjustedEnd`. For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * [App ID](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix) to filter events by.
   * @format GUID
   */
  appId?: string | null;
  /** Cursor to retrieve the next page of the results. */
  cursorPaging?: CommonCursorPaging;
  /**
   * Sorting for the results.
   * If not specified, results are sorted by `start` ascending.
   * For DESC order, events are sorted by `end` time in descending order.
   */
  sort?: Sorting;
}

/** TODO Diverge */
export interface CommonCursorPaging {
  /**
   * Number of events to return.
   * Defaults to `50`. Maximum `100`.
   * @min 1
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

/** TODO Diverge */
export interface Sorting {
  /**
   * The field to sort by.
   * Either `start` or `end`.
   * Default is `start`.
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface ListEventsByContactIdResponse {
  /** Retrieved events. */
  events?: Event[];
  /** Paging metadata. */
  pagingMetadata?: CommonCursorPagingMetadata;
}

/** TODO Diverge */
export interface CommonCursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Use these cursor to paginate between results. [Read more](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging). */
  cursors?: CommonCursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface CommonCursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
}

export interface ListEventsByMemberIdRequest {
  /**
   * ID of the [member](https://dev.wix.com/docs/rest/crm/members-contacts/members/members/member-object)
   * to retrieve events for. Required, unless you provide `cursorPaging`.
   * Provide `me` to retrieve events for the currently logged-in member.
   * You must have the `Read Calendars - Including PI`
   * [permission scope](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/configure-permissions-for-your-app)
   * to retrieve events for members who aren't the currently logged in member.
   * @minLength 2
   * @maxLength 36
   */
  memberId: string | null;
  /**
   * Local start date and time from which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`. Required if neither `cursorPaging` is provided nor a non-empty `eventIds` array.
   *
   * Events that start before the `fromLocalDate` but end after it are included in
   * the results. Must be earlier than `toLocalDate` unless the sort order is descending.
   * @format LOCAL_DATE_TIME
   */
  fromLocalDate?: string | null;
  /**
   * Local end date and time up to which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`. Can't be more than a full year after
   * `fromLocalDate`. Required if neither `cursorPaging` is provided nor a non-empty `eventIds` array.
   *
   * Events that start before `toLocalDate` but end after it are included in the
   * results. Must be later than `fromLocalDate` unless the sort order is descending.
   * @format LOCAL_DATE_TIME
   */
  toLocalDate?: string | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and
   * `adjustedEnd`. For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * [App ID](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix) to filter events by.
   * @format GUID
   */
  appId?: string | null;
  /** Cursor to retrieve the next page of the results. */
  cursorPaging?: CommonCursorPaging;
  /**
   * IDs of the events to retrieve.
   * If you provide a list of IDs, all other filters are ignored.
   *
   * Max: 100 `eventId`s
   * @maxSize 100
   * @minLength 36
   * @maxLength 250
   */
  eventIds?: string[];
  /**
   * Sorting for the results.
   * If not specified, results are sorted by `start` ascending.
   * For DESC order, events are sorted by `end` time in descending order.
   */
  sort?: Sorting;
}

export interface ListEventsByMemberIdResponse {
  /** Retrieved events. */
  events?: Event[];
  /** Paging metadata. */
  pagingMetadata?: CommonCursorPagingMetadata;
}

/** Encapsulates all details written to the Greyhound topic when a site's properties are updated. */
export interface SitePropertiesNotification {
  /** The site ID for which this update notification applies. */
  metasiteId?: string;
  /** The actual update event. */
  event?: SitePropertiesEvent;
  /**
   * A convenience set of mappings from the MetaSite ID to its constituent services.
   * @maxSize 500
   */
  translations?: Translation[];
  /** Context of the notification */
  changeContext?: ChangeContext;
}

/** The actual update event for a particular notification. */
export interface SitePropertiesEvent {
  /** Version of the site's properties represented by this update. */
  version?: number;
  /** Set of properties that were updated - corresponds to the fields in "properties". */
  fields?: string[];
  /** Updated properties. */
  properties?: Properties;
}

export interface Properties {
  /** Site categories. */
  categories?: Categories;
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   *
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   *
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   */
  paymentCurrency?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Fax number. */
  fax?: string | null;
  /** Address. */
  address?: Address;
  /** Site display name. */
  siteDisplayName?: string | null;
  /** Business name. */
  businessName?: string | null;
  /** Path to the site's logo in Wix Media (without Wix Media base URL). */
  logo?: string | null;
  /** Site description. */
  description?: string | null;
  /**
   * Business schedule. Regular and exceptional time periods when the business is open or the service is available.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /** Supported languages of a site and the primary language. */
  multilingual?: Multilingual;
  /** Cookie policy the Wix user defined for their site (before the site visitor interacts with/limits it). */
  consentPolicy?: ConsentPolicy;
  /**
   * Supported values: `FITNESS SERVICE`, `RESTAURANT`, `BLOG`, `STORE`, `EVENT`, `UNKNOWN`.
   *
   * Site business type.
   */
  businessConfig?: string | null;
  /** External site URL that uses Wix as its headless business solution. */
  externalSiteUrl?: string | null;
  /** Track clicks analytics. */
  trackClicksAnalytics?: boolean;
  /**
   * Company ID issued to the business by local authorities. Appears on invoices and price quotes.
   * @maxLength 50
   */
  companyId?: string | null;
}

export interface Categories {
  /** Primary site category. */
  primary?: string;
  /**
   * Secondary site category.
   * @maxSize 50
   */
  secondary?: string[];
  /** Business Term Id */
  businessTermId?: string | null;
}

export interface Locale {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string;
}

export interface Address {
  /** Street name. */
  street?: string;
  /** City name. */
  city?: string;
  /** Two-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format. */
  country?: string;
  /** State. */
  state?: string;
  /**
   * Zip or postal code.
   * @maxLength 20
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  isPhysical?: boolean;
  /** Google-formatted version of this address. */
  googleFormattedAddress?: string;
  /** Street number. */
  streetNumber?: string;
  /** Apartment number. */
  apartmentNumber?: string;
  /** Geographic coordinates of location. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition, the user can state where to display the additional description - before, after, or instead of the address string.
 */
export interface AddressHint {
  /** Extra text displayed next to, or instead of, the actual address. */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface Multilingual {
  /**
   * Supported languages list.
   * @maxSize 200
   */
  supportedLanguages?: SupportedLanguage[];
  /** Whether to redirect to user language. */
  autoRedirect?: boolean;
}

export interface SupportedLanguage {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Locale. */
  locale?: Locale;
  /** Whether the supported language is the primary language for the site. */
  isPrimary?: boolean;
  /** Language icon. */
  countryCode?: string;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
  /** Whether the supported language is the primary language for site visitors. */
  isVisitorPrimary?: boolean | null;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface ConsentPolicy {
  /** Whether the site uses cookies that are essential to site operation. Always `true`. */
  essential?: boolean | null;
  /** Whether the site uses cookies that affect site performance and other functional measurements. */
  functional?: boolean | null;
  /** Whether the site uses cookies that collect analytics about how the site is used (in order to improve it). */
  analytics?: boolean | null;
  /** Whether the site uses cookies that collect information allowing better customization of the experience for a current visitor. */
  advertising?: boolean | null;
  /** CCPA compliance flag. */
  dataToThirdParty?: boolean | null;
}

/** A single mapping from the MetaSite ID to a particular service. */
export interface Translation {
  /** The service type. */
  serviceType?: string;
  /** The application definition ID; this only applies to services of type ThirdPartyApps. */
  appDefId?: string;
  /** The instance ID of the service. */
  instanceId?: string;
}

export interface ChangeContext extends ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

/** @oneof */
export interface ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

export interface PropertiesChange {}

export interface SiteCreated {
  /** Origin template site id. */
  originTemplateId?: string | null;
}

export interface SiteCloned {
  /** Origin site id. */
  originMetaSiteId?: string;
}

export interface Empty {}

export interface GetEventRequest {
  /**
   * ID of the event to retrieve.
   *
   * Min: 36 characters
   * Max: 250 characters
   * @minLength 36
   * @maxLength 250
   */
  eventId: string | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * Information about which fields containing personal data to return. Refer to the
   * [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions)
   * for more information.
   *
   * Supported values:
   * + `PI_FIELDS`: Returns all fields with personal data. Your app must have `Read Calendars - Including PI` or `Manage Calendars` permission scope.
   * + `OWN_PI_FIELDS`: Returns only fields containing your own personal data.
   *
   * Max: 1 field
   * Default: No personal data is returned.
   * @maxSize 1
   */
  fields?: RequestedFieldsWithLiterals[];
}

export enum RequestedFields {
  /** Returns all fields with personal data. Your app must have `Read Calendars - Including PI` or `Manage Calendars` permission scope. */
  PI_FIELDS = 'PI_FIELDS',
  /** Returns only fields containing your own personal data. */
  OWN_PI_FIELDS = 'OWN_PI_FIELDS',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'PI_FIELDS'
  | 'OWN_PI_FIELDS';

export interface GetEventResponse {
  /** Retrieved event. */
  event?: Event;
}

export interface ListEventsRequest {
  /**
   * IDs of the events to retrieve.
   *
   * Min: 1 event ID
   * Max: 100 event IDs
   * @minSize 1
   * @maxSize 100
   * @minLength 36
   * @maxLength 250
   */
  eventIds: string[];
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * Information about which fields containing personal data to return. Refer to the
   * [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions)
   * for more information.
   *
   * Supported values:
   * + `PI_FIELDS`: Returns all fields with personal data. Your app must have `Read Calendars - Including PI` or `Manage Calendars` permission scope.
   * + `OWN_PI_FIELDS`: Returns only fields containing your own personal data.
   *
   * Max: 1 field
   * Default: No personal data is returned.
   * @maxSize 1
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface ListEventsResponse {
  /** Retrieved events matching the provided IDs. */
  events?: Event[];
}

export interface QueryEventsRequest {
  /**
   * Local start date and time from which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`.
   *
   * Events that start before the `fromLocalDate` but end after it are included in
   * the results. Must be earlier than `toLocalDate` unless the sort order is
   * descending.
   * @format LOCAL_DATE_TIME
   */
  fromLocalDate?: string | null;
  /**
   * Local end date and time up to which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`.
   *
   * Events that start before `toLocalDate` but end after it are included in the
   * results. Must be later than `fromLocalDate` unless the sort order is
   * descending.
   * @format LOCAL_DATE_TIME
   */
  toLocalDate?: string | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and
   * `adjustedEnd`. For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * Query containing filters and paging.
   * Do not specify filters for start and end dates inside `query.filter`, instead
   * specify `fromLocalDate` and `toLocalDate`.
   */
  query?: CursorQuery;
  /**
   * Filters events based on their `recurrenceType`.
   *
   * Max: 5 recurrence types can be specified.
   * Default: Events with `recurrenceType` of `NONE`, `INSTANCE`, and `EXCEPTION` are returned.
   * @maxSize 5
   */
  recurrenceType?: RecurrenceTypeWithLiterals[];
  /**
   * Information about which fields containing personal data to return. Refer to the
   * [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions)
   * for more information.
   *
   * Supported values:
   * + `PI_FIELDS`: Returns all fields with personal data. Your app must have `Read Calendars - Including PI` or `Manage Calendars` permission scope.
   * + `OWN_PI_FIELDS`: Returns only fields containing your own personal data.
   *
   * Max: 1 field
   * Default: No personal data is returned.
   * @maxSize 1
   */
  fields?: RequestedFieldsWithLiterals[];
}

/** TODO Diverge */
export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language)
   * for more information.
   *
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`.
   *
   * Do not specify filters for start and end dates inside `query.filter`, instead
   * specify `fromLocalDate` and `toLocalDate`.
   */
  filter?: Record<string, any> | null;
  /**
   * Whether to sort events by their start date in ascending order or by their end date in descending order.
   * Default is start ascending.
   * @maxSize 1
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter`. */
  cursorPaging?: CursorPaging;
}

/** TODO Diverge */
export interface CursorPaging {
  /**
   * Number of events to return.
   * Defaults to `50`. Maximum `1000`.
   * @min 1
   * @max 1000
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryEventsResponse {
  /** Retrieved events matching the provided query. */
  events?: Event[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

/** TODO Diverge */
export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Use these cursor to paginate between results. [Read more](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging). */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
}

export interface ListRecurringEventInstancesHistoryRequest {
  /**
   * The ID of the recurring event.
   * Required, unless `cursorPaging` is provided.
   * @minLength 64
   * @maxLength 64
   */
  recurringEventId?: string | null;
  /**
   * The revision of the recurring event.
   * Required, unless `cursorPaging` is provided.
   */
  revision?: string | null;
  /**
   * Inclusive start date for which events are returned, in ISO-8601 format.
   * Events that begin at or after the `fromDate` are included in the results.
   * Required, unless `cursorPaging` is provided.
   */
  fromDate?: Date | null;
  /**
   * Exclusive end date for which events are returned, in ISO-8601 format.
   * Events that begin before the `toDate` are included in the results.
   * Required, unless `cursorPaging` is provided.
   */
  toDate?: Date | null;
  /** Optional cursor pointing to the next page of events. */
  cursorPaging?: CursorPaging;
}

export interface ListRecurringEventInstancesHistoryResponse {
  /** The recurring event instances. */
  recurringEventInstances?: Event[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CreateEventRequest {
  /** Event to create. */
  event: Event;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * Idempotency key guaranteeing that you don't create the same event more than once.
   * Must be a valid UUID/GUID, for example `550e8400-e29b-41d4-a716-446655440000`.
   * @format GUID
   */
  idempotencyKey?: string | null;
}

export interface CreateEventResponse {
  /** Created event. */
  event?: Event;
}

export interface BulkCreateEventRequest {
  /**
   * Events to create.
   * @minSize 1
   * @maxSize 50
   */
  events: MaskedEvent[];
  /**
   * Whether to return created events.
   *
   * Default: `false`.
   */
  returnEntity?: boolean | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

export interface MaskedEvent {
  /** Event to create. */
  event?: Event;
}

export interface BulkCreateEventResponse {
  /** The result for each event, containing the event and whether the action was successful. */
  results?: BulkEventResult[];
  /** Total successes and failures. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkEventResult {
  /** Whether the requested action was successful. */
  itemMetadata?: ItemMetadata;
  /** The event. */
  item?: Event;
}

export interface ItemMetadata {
  /**
   * Event ID.
   * Should always be available, unless it's impossible (for example, when failing to create an event).
   */
  _id?: string | null;
  /** Index of the item within the request array, for correlation between request and response items. */
  originalIndex?: number;
  /**
   * Whether the requested action was successful for this event.
   * When `false`, the `error` field is populated.
   */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of events that were successfully processed. */
  totalSuccesses?: number;
  /** Number of events that couldn't be processed. */
  totalFailures?: number;
}

export interface UpdateEventRequest {
  /** Event to update. */
  event: Event;
  /**
   * Information about whether participants of the updated event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

export interface UpdateEventResponse {
  /** Updated event. */
  event?: Event;
}

export interface BulkUpdateEventRequest {
  /**
   * Events to update.
   *
   * Min: 1 event
   * Max: 50 events
   * @minSize 1
   * @maxSize 50
   */
  events: BulkUpdateEventRequestMaskedEvent[];
  /**
   * Whether to return the updated events.
   *
   * Default: `false`
   */
  returnEntity?: boolean | null;
  /**
   * Information about whether participants of the updated event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

export interface BulkUpdateEventRequestMaskedEvent {
  /** Event to update. */
  event?: Event;
}

export interface BulkUpdateEventResponse {
  /** The result for each event, containing the event and whether the action was successful. */
  results?: BulkEventResult[];
  /** Total successes and failures. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface UpdateEventParticipantsRequest {
  /**
   * ID of the event to update.
   *
   * Min: 36 characters
   * Max: 250 characters
   * @minLength 36
   * @maxLength 250
   */
  eventId?: string | null;
  /** The participants to update. */
  participants?: Participants;
}

export interface UpdateEventParticipantsResponse {
  /** Updated event. */
  event?: Event;
}

export interface RestoreEventDefaultsRequest {
  /**
   * ID of the event for which to restore default values.
   * @minLength 36
   * @maxLength 250
   */
  eventId: string | null;
  /**
   * Fields for which to restore default values.
   *
   * `TIME` restores default values for `start` and `end`.
   *
   * Min: 1 field
   * @minSize 1
   */
  fields: FieldWithLiterals[];
  /**
   * Information about whether participants of the updated event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

export interface RestoreEventDefaultsResponse {
  /** Updated event. */
  event?: Event;
}

export interface SplitRecurringEventRequest {
  /**
   * ID of the `MASTER` event to split.
   * @minLength 36
   * @maxLength 250
   */
  recurringEventId: string | null;
  /**
   * Local date and time at which the `MASTER` event is split in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2025-03-11T09:00:00`.
   * Must be a future date that's after the `start` date of the next `INSTANCE` or
   * `EXCEPTION` event in the series. Additionally, there must be another
   * `INSTANCE` or `EXCEPTION` event following this next event, as the changes
   * wouldn't affect any event without a subsequent occurrence.
   * @format LOCAL_DATE_TIME
   */
  splitLocalDate: string | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

export interface SplitRecurringEventResponse {
  /** Original `MASTER` event that was shortened. */
  updatedRecurringEventEndingBeforeSplit?: Event;
  /** New `MASTER` event starting with the first event after the split date. */
  newRecurringEventStartingFromSplit?: Event;
}

export interface CancelEventRequest {
  /**
   * ID of the event to cancel.
   * @minLength 36
   * @maxLength 250
   */
  eventId: string | null;
  /**
   * Information about whether participants of the canceled event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

export interface CancelEventResponse {
  /** Canceled event. */
  event?: Event;
}

export interface BulkCancelEventRequest {
  /**
   * IDs of the events to cancel.
   * @minSize 1
   * @maxSize 50
   */
  eventIds: string[];
  /**
   * Whether to return the canceled events.
   *
   * Default: `false`
   */
  returnEntity?: boolean | null;
  /**
   * Information about whether participants of the canceled events are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

export interface BulkCancelEventResponse {
  /** The result for each event, containing the event and whether the action was successful. */
  results?: BulkEventResult[];
  /** Total successes and failures. */
  bulkActionMetadata?: BulkActionMetadata;
}

/** @docsIgnore */
export type CancelEventApplicationErrors = {
  code?: 'EVENT_ALREADY_CANCELLED';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkCancelEventApplicationErrors = {
  code?: 'EVENT_ALREADY_CANCELLED';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface EventCancelledEnvelope {
  data: EventCancelled;
  metadata: EventMetadata;
}

/**
 * Triggered when an event is canceled.
 *
 * Not triggered when `INSTANCE` events are canceled. You can use the
 * [Events View API](https://dev.wix.com/docs/rest/business-management/calendar/event-views-v3/introduction)
 * to get notified about `INSTANCE` events.
 * @permissionScope Read Calendar - Including PI
 * @permissionScopeId SCOPE.DC-CALENDAR.READ-PI
 * @permissionScope Manage Calendars
 * @permissionScopeId SCOPE.DC-CALENDAR.MANAGE
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Read bookings calendar - including participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-CALENDAR-WITH-PARTICIPANTS
 * @permissionId CALENDAR.EVENT_READ_PI
 * @webhook
 * @eventType wix.calendar.v3.event_cancelled
 * @slug cancelled
 */
export declare function onEventCancelled(
  handler: (event: EventCancelledEnvelope) => void | Promise<void>
): void;

export interface EventCreatedEnvelope {
  entity: Event;
  metadata: EventMetadata;
}

/**
 * Triggered when an event is created, including when an existing `MASTER`
 * event is split.
 *
 * Not triggered when `INSTANCE` events are created. You can use the
 * [Events View API](https://dev.wix.com/docs/rest/business-management/calendar/event-views-v3/introduction)
 * to get notified about `INSTANCE` events.
 * @permissionScope Read Calendar - Including PI
 * @permissionScopeId SCOPE.DC-CALENDAR.READ-PI
 * @permissionScope Manage Calendars
 * @permissionScopeId SCOPE.DC-CALENDAR.MANAGE
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Read bookings calendar - including participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-CALENDAR-WITH-PARTICIPANTS
 * @permissionId CALENDAR.EVENT_READ_PI
 * @webhook
 * @eventType wix.calendar.v3.event_created
 * @slug created
 */
export declare function onEventCreated(
  handler: (event: EventCreatedEnvelope) => void | Promise<void>
): void;

export interface EventRecurringSplitEnvelope {
  data: RecurringEventSplit;
  metadata: EventMetadata;
}

/**
 * Triggered when a `MASTER` event is split.
 * @permissionScope Read Calendar - Including PI
 * @permissionScopeId SCOPE.DC-CALENDAR.READ-PI
 * @permissionScope Manage Calendars
 * @permissionScopeId SCOPE.DC-CALENDAR.MANAGE
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Read bookings calendar - including participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-CALENDAR-WITH-PARTICIPANTS
 * @permissionId CALENDAR.EVENT_READ_PI
 * @webhook
 * @eventType wix.calendar.v3.event_recurring_split
 * @slug recurring_split
 */
export declare function onEventRecurringSplit(
  handler: (event: EventRecurringSplitEnvelope) => void | Promise<void>
): void;

export interface EventUpdatedEnvelope {
  entity: Event;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when an event is updated, including when it's canceled.
 *
 * Not triggered when `INSTANCE` events are updated. You can use the
 * [Events View API](https://dev.wix.com/docs/rest/business-management/calendar/event-views-v3/introduction)
 * to get notified about `INSTANCE` events.
 * @permissionScope Read Calendar - Including PI
 * @permissionScopeId SCOPE.DC-CALENDAR.READ-PI
 * @permissionScope Manage Calendars
 * @permissionScopeId SCOPE.DC-CALENDAR.MANAGE
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionScope Read bookings calendar - including participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-CALENDAR-WITH-PARTICIPANTS
 * @permissionId CALENDAR.EVENT_READ_PI
 * @webhook
 * @eventType wix.calendar.v3.event_updated
 * @slug updated
 */
export declare function onEventUpdated(
  handler: (event: EventUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves a list of events filtered by the participant's
 * [contact](https://dev.wix.com/docs/sdk/backend-modules/crm/contacts/introduction)
 * ID.
 *
 *
 * You can't set `toLocalDate` to a value that's more than a full year after
 * `fromLocalDate`.
 * @param contactId - ID of the [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)
 * to retrieve events for. Required, unless you provide `cursorPaging`.
 * @public
 * @requiredField contactId
 * @permissionId CALENDAR.EVENT_READ_PI
 * @applicableIdentity APP
 * @fqn wix.calendar.participantevents.v3.ParticipantEventsService.ListEventsByContactId
 */
export async function listEventsByContactId(
  contactId: string,
  options?: ListEventsByContactIdOptions
): Promise<
  NonNullablePaths<
    ListEventsByContactIdResponse,
    | `events`
    | `events.${number}.status`
    | `events.${number}.recurrenceType`
    | `events.${number}.recurrenceRule.frequency`
    | `events.${number}.transparency`
    | `events.${number}.location.type`
    | `events.${number}.participants.status`
    | `events.${number}.conferencingDetails.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    contactId: contactId,
    fromLocalDate: options?.fromLocalDate,
    toLocalDate: options?.toLocalDate,
    timeZone: options?.timeZone,
    appId: options?.appId,
    cursorPaging: options?.cursorPaging,
    sort: options?.sort,
  });

  const reqOpts = ambassadorWixCalendarV3Event.listEventsByContactId(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          contactId: '$[0]',
          fromLocalDate: '$[1].fromLocalDate',
          toLocalDate: '$[1].toLocalDate',
          timeZone: '$[1].timeZone',
          appId: '$[1].appId',
          cursorPaging: '$[1].cursorPaging',
          sort: '$[1].sort',
        },
        singleArgumentUnchanged: false,
      },
      ['contactId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListEventsByContactIdOptions {
  /**
   * Local start date and time from which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`. Required if `cursorPaging` isn't provided.
   *
   * Events that start before the `fromLocalDate` but end after it are included in
   * the results. Must be earlier than `toLocalDate` unless the sort order is descending.
   * @format LOCAL_DATE_TIME
   */
  fromLocalDate?: string | null;
  /**
   * Local end date and time up to which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`. Can't be more than a full year after
   * `2024-01-30T13:30:00`. Required if `cursorPaging` isn't provided.
   *
   * Events that start before `toLocalDate` but end after it are included in the
   * results. Must be later than `fromLocalDate` unless the sort order is descending.
   * @format LOCAL_DATE_TIME
   */
  toLocalDate?: string | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and
   * `adjustedEnd`. For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * [App ID](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix) to filter events by.
   * @format GUID
   */
  appId?: string | null;
  /** Cursor to retrieve the next page of the results. */
  cursorPaging?: CommonCursorPaging;
  /**
   * Sorting for the results.
   * If not specified, results are sorted by `start` ascending.
   * For DESC order, events are sorted by `end` time in descending order.
   */
  sort?: Sorting;
}

/**
 * Retrieves a list of events filtered by the participant's
 * [member](https://dev.wix.com/docs/sdk/backend-modules/members/members/introduction)
 * ID.
 *
 *
 * You can't set `toLocalDate` to a value that's more than a full year after
 * `fromLocalDate`.
 * @param memberId - ID of the [member](https://dev.wix.com/docs/rest/crm/members-contacts/members/members/member-object)
 * to retrieve events for. Required, unless you provide `cursorPaging`.
 * Provide `me` to retrieve events for the currently logged-in member.
 * You must have the `Read Calendars - Including PI`
 * [permission scope](https://dev.wix.com/docs/build-apps/develop-your-app/access/authorization/configure-permissions-for-your-app)
 * to retrieve events for members who aren't the currently logged in member.
 * @public
 * @requiredField memberId
 * @permissionId CALENDAR.EVENT_READ
 * @permissionId CALENDAR.EVENT_READ_PI
 * @applicableIdentity APP
 * @fqn wix.calendar.participantevents.v3.ParticipantEventsService.ListEventsByMemberId
 */
export async function listEventsByMemberId(
  memberId: string,
  options?: ListEventsByMemberIdOptions
): Promise<
  NonNullablePaths<
    ListEventsByMemberIdResponse,
    | `events`
    | `events.${number}.status`
    | `events.${number}.recurrenceType`
    | `events.${number}.recurrenceRule.frequency`
    | `events.${number}.transparency`
    | `events.${number}.location.type`
    | `events.${number}.participants.status`
    | `events.${number}.conferencingDetails.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    memberId: memberId,
    fromLocalDate: options?.fromLocalDate,
    toLocalDate: options?.toLocalDate,
    timeZone: options?.timeZone,
    appId: options?.appId,
    cursorPaging: options?.cursorPaging,
    eventIds: options?.eventIds,
    sort: options?.sort,
  });

  const reqOpts = ambassadorWixCalendarV3Event.listEventsByMemberId(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          memberId: '$[0]',
          fromLocalDate: '$[1].fromLocalDate',
          toLocalDate: '$[1].toLocalDate',
          timeZone: '$[1].timeZone',
          appId: '$[1].appId',
          cursorPaging: '$[1].cursorPaging',
          eventIds: '$[1].eventIds',
          sort: '$[1].sort',
        },
        singleArgumentUnchanged: false,
      },
      ['memberId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListEventsByMemberIdOptions {
  /**
   * Local start date and time from which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`. Required if neither `cursorPaging` is provided nor a non-empty `eventIds` array.
   *
   * Events that start before the `fromLocalDate` but end after it are included in
   * the results. Must be earlier than `toLocalDate` unless the sort order is descending.
   * @format LOCAL_DATE_TIME
   */
  fromLocalDate?: string | null;
  /**
   * Local end date and time up to which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`. Can't be more than a full year after
   * `fromLocalDate`. Required if neither `cursorPaging` is provided nor a non-empty `eventIds` array.
   *
   * Events that start before `toLocalDate` but end after it are included in the
   * results. Must be later than `fromLocalDate` unless the sort order is descending.
   * @format LOCAL_DATE_TIME
   */
  toLocalDate?: string | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and
   * `adjustedEnd`. For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * [App ID](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix) to filter events by.
   * @format GUID
   */
  appId?: string | null;
  /** Cursor to retrieve the next page of the results. */
  cursorPaging?: CommonCursorPaging;
  /**
   * IDs of the events to retrieve.
   * If you provide a list of IDs, all other filters are ignored.
   *
   * Max: 100 `eventId`s
   * @maxSize 100
   * @minLength 36
   * @maxLength 250
   */
  eventIds?: string[];
  /**
   * Sorting for the results.
   * If not specified, results are sorted by `start` ascending.
   * For DESC order, events are sorted by `end` time in descending order.
   */
  sort?: Sorting;
}

/**
 * Retrieves an event.
 * @param eventId - ID of the event to retrieve.
 *
 * Min: 36 characters
 * Max: 250 characters
 * @public
 * @requiredField eventId
 * @permissionId CALENDAR.EVENT_READ
 * @permissionId CALENDAR.EVENT_READ_PI
 * @applicableIdentity APP
 * @returns Retrieved event.
 * @fqn wix.calendar.events.v3.EventsService.GetEvent
 */
export async function getEvent(
  eventId: string,
  options?: GetEventOptions
): Promise<
  NonNullablePaths<
    Event,
    | `status`
    | `recurrenceType`
    | `recurrenceRule.frequency`
    | `recurrenceRule.days`
    | `transparency`
    | `location.type`
    | `resources`
    | `resources.${number}.transparency`
    | `resources.${number}.permissionRole`
    | `participants.list`
    | `participants.status`
    | `conferencingDetails.type`
    | `inheritedFields`
    | `permissions`
    | `permissions.${number}.identity.wixUserId`
    | `permissions.${number}.role`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    timeZone: options?.timeZone,
    fields: options?.fields,
  });

  const reqOpts = ambassadorWixCalendarV3Event.getEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.event!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          timeZone: '$[1].timeZone',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetEventOptions {
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * Information about which fields containing personal data to return. Refer to the
   * [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions)
   * for more information.
   *
   * Supported values:
   * + `PI_FIELDS`: Returns all fields with personal data. Your app must have `Read Calendars - Including PI` or `Manage Calendars` permission scope.
   * + `OWN_PI_FIELDS`: Returns only fields containing your own personal data.
   *
   * Max: 1 field
   * Default: No personal data is returned.
   * @maxSize 1
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Retrieves a list of events by their IDs.
 * @param eventIds - IDs of the events to retrieve.
 *
 * Min: 1 event ID
 * Max: 100 event IDs
 * @public
 * @requiredField eventIds
 * @permissionId CALENDAR.EVENT_READ
 * @permissionId CALENDAR.EVENT_READ_PI
 * @applicableIdentity APP
 * @fqn wix.calendar.events.v3.EventsService.ListEvents
 */
export async function listEvents(
  eventIds: string[],
  options?: ListEventsOptions
): Promise<
  NonNullablePaths<
    ListEventsResponse,
    | `events`
    | `events.${number}.status`
    | `events.${number}.recurrenceType`
    | `events.${number}.recurrenceRule.frequency`
    | `events.${number}.transparency`
    | `events.${number}.location.type`
    | `events.${number}.participants.status`
    | `events.${number}.conferencingDetails.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventIds: eventIds,
    timeZone: options?.timeZone,
    fields: options?.fields,
  });

  const reqOpts = ambassadorWixCalendarV3Event.listEvents(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventIds: '$[0]',
          timeZone: '$[1].timeZone',
          fields: '$[1].fields',
        },
        singleArgumentUnchanged: false,
      },
      ['eventIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListEventsOptions {
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * Information about which fields containing personal data to return. Refer to the
   * [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions)
   * for more information.
   *
   * Supported values:
   * + `PI_FIELDS`: Returns all fields with personal data. Your app must have `Read Calendars - Including PI` or `Manage Calendars` permission scope.
   * + `OWN_PI_FIELDS`: Returns only fields containing your own personal data.
   *
   * Max: 1 field
   * Default: No personal data is returned.
   * @maxSize 1
   */
  fields?: RequestedFieldsWithLiterals[];
}

/**
 * Creates a query to retrieve a list of events.
 *
 * The `queryEvents()` function builds a query to retrieve a list of events and returns an `EventsQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/events-query-builder/find) function.
 *
 * You can refine the query by chaining `EventsQueryBuilder` functions onto the query. `EventsQueryBuilder` functions enable you to sort, filter, and control the results that `queryEvents()` returns.
 *
 * `queryEvents()` runs with the following `EventsQueryBuilder` default that you can override:
 *
 * + `limit` is `50`.
 * + Sorted by `event.start` in ascending order.
 *
 * The functions that are chained to `queryEvents()` are applied in the order they are called.
 *
 * The following `EventsQueryBuilder` functions are supported for the `queryEvents()` function. For a full description of the events object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/events-query-result/items) property in `EventsQueryResult`.
 *
 * @public
 * @permissionId CALENDAR.EVENT_READ
 * @permissionId CALENDAR.EVENT_READ_PI
 * @applicableIdentity APP
 * @fqn wix.calendar.events.v3.EventsService.QueryEvents
 */
export function queryEvents(options?: QueryEventsOptions): EventsQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Event, 'CURSOR', QueryEventsRequest, QueryEventsResponse>(
    {
      func: async (payload: QueryEventsRequest) => {
        const reqOpts = ambassadorWixCalendarV3Event.queryEvents({
          ...payload,
          ...(options ?? {}),
        });

        sideEffects?.onSiteCall?.();
        try {
          const result = await httpClient.request(reqOpts);
          sideEffects?.onSuccess?.(result);
          return result;
        } catch (err) {
          sideEffects?.onError?.(err);
          throw err;
        }
      },
      requestTransformer: (query: QueryEventsRequest['query']) => {
        const args = [query, options] as [
          QueryEventsRequest['query'],
          QueryEventsOptions
        ];
        return renameKeysFromSDKRequestToRESTRequest({
          ...args?.[1],
          query: args?.[0],
        });
      },
      responseTransformer: ({ data }: HttpResponse<QueryEventsResponse>) => {
        const transformedData = renameKeysFromRESTResponseToSDKResponse(
          transformPaths(data, [])
        );

        return {
          items: transformedData?.events,
          pagingMetadata: transformedData?.pagingMetadata,
        };
      },
      errorTransformer: (err: unknown) => {
        const transformedError = sdkTransformError(err, {
          spreadPathsToArguments: {},
          explicitPathsToArguments: { query: '$[0]' },
          singleArgumentUnchanged: false,
        });

        throw transformedError;
      },
      pagingMethod: 'CURSOR',
      transformationPaths: {},
    }
  );
}

export interface QueryEventsOptions {
  /**
   * Local start date and time from which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`.
   *
   * Events that start before the `fromLocalDate` but end after it are included in
   * the results. Must be earlier than `toLocalDate` unless the sort order is
   * descending.
   * @format LOCAL_DATE_TIME
   */
  fromLocalDate?: string | null | undefined;
  /**
   * Local end date and time up to which events are returned in
   * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
   * `2024-01-30T13:30:00`.
   *
   * Events that start before `toLocalDate` but end after it are included in the
   * results. Must be later than `fromLocalDate` unless the sort order is
   * descending.
   * @format LOCAL_DATE_TIME
   */
  toLocalDate?: string | null | undefined;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for `fromLocalDate`, `toLocalDate`, and for calculating `adjustedStart` and
   * `adjustedEnd`. For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   */
  timeZone?: string | null | undefined;
  /**
   * Filters events based on their `recurrenceType`.
   *
   * Max: 5 recurrence types can be specified.
   * Default: Events with `recurrenceType` of `NONE`, `INSTANCE`, and `EXCEPTION` are returned.
   * @maxSize 5
   */
  recurrenceType?: RecurrenceTypeWithLiterals[] | undefined;
  /**
   * Information about which fields containing personal data to return. Refer to the
   * [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions)
   * for more information.
   *
   * Supported values:
   * + `PI_FIELDS`: Returns all fields with personal data. Your app must have `Read Calendars - Including PI` or `Manage Calendars` permission scope.
   * + `OWN_PI_FIELDS`: Returns only fields containing your own personal data.
   *
   * Max: 1 field
   * Default: No personal data is returned.
   * @maxSize 1
   */
  fields?: RequestedFieldsWithLiterals[] | undefined;
}

interface QueryCursorResult {
  cursors: CommonCursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface EventsQueryResult extends QueryCursorResult {
  items: Event[];
  query: EventsQueryBuilder;
  next: () => Promise<EventsQueryResult>;
  prev: () => Promise<EventsQueryResult>;
}

export interface EventsQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | 'scheduleId'
      | 'externalScheduleId'
      | 'type'
      | 'recurringEventId'
      | 'transparency'
      | 'location.id'
      | 'location.type'
      | 'totalCapacity'
      | 'remainingCapacity'
      | 'participants.total'
      | 'appId',
    value: any
  ) => EventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName: 'totalCapacity' | 'remainingCapacity' | 'participants.total',
    value: any
  ) => EventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: 'totalCapacity' | 'remainingCapacity' | 'participants.total',
    value: any
  ) => EventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: 'totalCapacity' | 'remainingCapacity' | 'participants.total',
    value: any
  ) => EventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: 'totalCapacity' | 'remainingCapacity' | 'participants.total',
    value: any
  ) => EventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: 'totalCapacity' | 'remainingCapacity' | 'participants.total',
    value: any
  ) => EventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (propertyName: string, value: any[]) => EventsQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (propertyName: string, value: any[]) => EventsQueryBuilder;
  in: (
    propertyName:
      | 'scheduleId'
      | 'externalScheduleId'
      | 'type'
      | 'recurringEventId'
      | 'location.id'
      | 'location.type'
      | 'appId',
    value: any
  ) => EventsQueryBuilder;
  exists: (
    propertyName: 'location' | 'totalCapacity' | 'conferencingDetails',
    value: boolean
  ) => EventsQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => EventsQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => EventsQueryBuilder;
  find: () => Promise<EventsQueryResult>;
}

/**
 * @hidden
 * @fqn wix.calendar.events.v3.EventsService.QueryEvents
 * @requiredField query
 */
export async function typedQueryEvents(
  query: EventQuery,
  options?: QueryEventsOptions
): Promise<
  NonNullablePaths<
    QueryEventsResponse,
    | `events`
    | `events.${number}.status`
    | `events.${number}.recurrenceType`
    | `events.${number}.recurrenceRule.frequency`
    | `events.${number}.transparency`
    | `events.${number}.location.type`
    | `events.${number}.participants.status`
    | `events.${number}.conferencingDetails.type`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts = ambassadorWixCalendarV3Event.queryEvents(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface EventQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: ['transparency'];
      operators: ['$eq'];
      sort: 'NONE';
    },
    {
      fields: [
        'appId',
        'externalScheduleId',
        'location._id',
        'location.type',
        'recurringEventId',
        'scheduleId',
        'type'
      ];
      operators: ['$eq', '$in'];
      sort: 'NONE';
    },
    {
      fields: ['participants.total', 'remainingCapacity'];
      operators: ['$eq', '$gt', '$gte', '$lt', '$lte', '$ne'];
      sort: 'NONE';
    },
    {
      fields: ['totalCapacity'];
      operators: ['$eq', '$exists', '$gt', '$gte', '$lt', '$lte', '$ne'];
      sort: 'NONE';
    },
    {
      fields: ['resources._id', 'resources.transparency', 'resources.type'];
      operators: ['$hasAll', '$hasSome'];
      sort: 'NONE';
    },
    {
      fields: ['conferencingDetails', 'location'];
      operators: ['$exists'];
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Event, EventQuerySpec>;
export type EventQuery = {
  /** 
  Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter`.  
  */
  cursorPaging?: {
    /** 
  Number of events to return.
  Defaults to `50`. Maximum `1000`. 
  @min: 1,
  @max: 1000 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request.  
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.
  See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language)
  for more information.

  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`.

  Do not specify filters for start and end dates inside `query.filter`, instead
  specify `fromLocalDate` and `toLocalDate`.  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Whether to sort events by their start date in ascending order or by their end date in descending order.
  Default is start ascending. 
  @maxSize: 1 
  */
  sort?: {
    /** 
  The field to sort by.
  Either `start` or `end`.
  Default is `start`.  
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Event, EventQuerySpec, EventQuery>(),
  },
};

/**
 * Creates an event.
 *
 *
 * If you provide `recurrenceRule`, an event with `{"recurrenceType": "MASTER"}` is created. Then, you can't set `start.localDate` to a past date, though the time can be earlier on the same day. You can, however, create single-occurrence events for past dates.
 *
 * You can't create an event with `recurrenceType` set to `INSTANCE` or `EXCEPTION`. `INSTANCE` events are generated automatically based on the recurrence rule in the relevant `MASTER` event. `EXCEPTION` events are automatically created when you [update](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event) an `INSTANCE` event, which changes its `recurrenceType` to `EXCEPTION`.
 * @param event - Event to create.
 * @public
 * @requiredField event
 * @requiredField event.conferencingDetails.guestUrl
 * @requiredField event.conferencingDetails.hostUrl
 * @requiredField event.end
 * @requiredField event.end.localDate
 * @requiredField event.location.type
 * @requiredField event.participants.list.name
 * @requiredField event.recurrenceRule.days
 * @requiredField event.recurrenceRule.frequency
 * @requiredField event.recurrenceRule.until.localDate
 * @requiredField event.resources._id
 * @requiredField event.scheduleId
 * @requiredField event.start
 * @requiredField event.start.localDate
 * @permissionId CALENDAR.EVENT_CREATE_OWN
 * @permissionId CALENDAR.EVENT_CREATE
 * @applicableIdentity APP
 * @returns Created event.
 * @fqn wix.calendar.events.v3.EventsService.CreateEvent
 */
export async function createEvent(
  event: NonNullablePaths<
    Event,
    | `conferencingDetails.guestUrl`
    | `conferencingDetails.hostUrl`
    | `end`
    | `end.localDate`
    | `location.type`
    | `participants.list.${number}.name`
    | `recurrenceRule.days`
    | `recurrenceRule.frequency`
    | `recurrenceRule.until.localDate`
    | `resources.${number}._id`
    | `scheduleId`
    | `start`
    | `start.localDate`,
    5
  >,
  options?: CreateEventOptions
): Promise<
  NonNullablePaths<
    Event,
    | `status`
    | `recurrenceType`
    | `recurrenceRule.frequency`
    | `recurrenceRule.days`
    | `transparency`
    | `location.type`
    | `resources`
    | `resources.${number}.transparency`
    | `resources.${number}.permissionRole`
    | `participants.list`
    | `participants.status`
    | `conferencingDetails.type`
    | `inheritedFields`
    | `permissions`
    | `permissions.${number}.identity.wixUserId`
    | `permissions.${number}.role`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    event: event,
    timeZone: options?.timeZone,
    idempotencyKey: options?.idempotencyKey,
  });

  const reqOpts = ambassadorWixCalendarV3Event.createEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.event!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          event: '$[0]',
          timeZone: '$[1].timeZone',
          idempotencyKey: '$[1].idempotencyKey',
        },
        singleArgumentUnchanged: false,
      },
      ['event', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateEventOptions {
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
  /**
   * Idempotency key guaranteeing that you don't create the same event more than once.
   * Must be a valid UUID/GUID, for example `550e8400-e29b-41d4-a716-446655440000`.
   * @format GUID
   */
  idempotencyKey?: string | null;
}

/**
 * Creates multiple events in bulk.
 *
 *
 * If you provide `recurrenceRule`, an event with `{"recurrenceType": "MASTER"}` is
 * created. Then, you can't set `start.localDate` to a past date, though the time can be
 * earlier on the same day. You can, however, create single-occurrence events for
 * past dates.
 *
 * You can't create an event with `recurrenceType` set to `INSTANCE` or `EXCEPTION`.
 * `INSTANCE` events are generated automatically based on the recurrence rule in the
 * relevant `MASTER` event. `EXCEPTION` events are automatically created when you
 * [update](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event)
 * an `INSTANCE` event, which changes its `recurrenceType` to `EXCEPTION`.
 * @param events - Events to create.
 * @public
 * @requiredField events
 * @requiredField events.event
 * @requiredField events.event.conferencingDetails.guestUrl
 * @requiredField events.event.conferencingDetails.hostUrl
 * @requiredField events.event.end
 * @requiredField events.event.end.localDate
 * @requiredField events.event.location.type
 * @requiredField events.event.participants.list.name
 * @requiredField events.event.recurrenceRule.days
 * @requiredField events.event.recurrenceRule.frequency
 * @requiredField events.event.recurrenceRule.until.localDate
 * @requiredField events.event.resources._id
 * @requiredField events.event.scheduleId
 * @requiredField events.event.start
 * @requiredField events.event.start.localDate
 * @permissionId CALENDAR.EVENT_CREATE_OWN
 * @permissionId CALENDAR.EVENT_CREATE
 * @applicableIdentity APP
 * @fqn wix.calendar.events.v3.EventsService.BulkCreateEvent
 */
export async function bulkCreateEvent(
  events: NonNullablePaths<
    MaskedEvent,
    | `event`
    | `event.conferencingDetails.guestUrl`
    | `event.conferencingDetails.hostUrl`
    | `event.end`
    | `event.end.localDate`
    | `event.location.type`
    | `event.participants.list.${number}.name`
    | `event.recurrenceRule.days`
    | `event.recurrenceRule.frequency`
    | `event.recurrenceRule.until.localDate`
    | `event.resources.${number}._id`
    | `event.scheduleId`
    | `event.start`
    | `event.start.localDate`,
    6
  >[],
  options?: BulkCreateEventOptions
): Promise<
  NonNullablePaths<
    BulkCreateEventResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.status`
    | `results.${number}.item.recurrenceType`
    | `results.${number}.item.recurrenceRule.frequency`
    | `results.${number}.item.transparency`
    | `results.${number}.item.location.type`
    | `results.${number}.item.participants.status`
    | `results.${number}.item.conferencingDetails.type`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    events: events,
    returnEntity: options?.returnEntity,
    timeZone: options?.timeZone,
  });

  const reqOpts = ambassadorWixCalendarV3Event.bulkCreateEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          events: '$[0]',
          returnEntity: '$[1].returnEntity',
          timeZone: '$[1].timeZone',
        },
        singleArgumentUnchanged: false,
      },
      ['events', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateEventOptions {
  /**
   * Whether to return created events.
   *
   * Default: `false`.
   */
  returnEntity?: boolean | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

/**
 * Updates an event.
 *
 *
 * When updating an event with `{"recurrenceType": "INSTANCE"}`, the
 * `recurrenceType` automatically changes to `EXCEPTION`.
 *
 * When updating an event with `{"recurrenceType": "MASTER"}`:
 * + Past `INSTANCE` and `EXCEPTION` events remain unchanged.
 * + For future `INSTANCE` events, all relevant fields are updated.
 * + For future `EXCEPTION` events, only changes to the `inheritedFields` are
 * applied, while other fields remain unchanged.
 * @param _id - Event ID.
 * @public
 * @requiredField _id
 * @requiredField event
 * @requiredField event.conferencingDetails.guestUrl
 * @requiredField event.conferencingDetails.hostUrl
 * @requiredField event.end.localDate
 * @requiredField event.location.type
 * @requiredField event.participants.list.name
 * @requiredField event.recurrenceRule.days
 * @requiredField event.recurrenceRule.frequency
 * @requiredField event.recurrenceRule.until.localDate
 * @requiredField event.resources._id
 * @requiredField event.revision
 * @requiredField event.start.localDate
 * @permissionId CALENDAR.EVENT_UPDATE_OWN
 * @permissionId CALENDAR.EVENT_UPDATE
 * @applicableIdentity APP
 * @returns Updated event.
 * @fqn wix.calendar.events.v3.EventsService.UpdateEvent
 */
export async function updateEvent(
  _id: string,
  event: NonNullablePaths<
    UpdateEvent,
    | `conferencingDetails.guestUrl`
    | `conferencingDetails.hostUrl`
    | `end.localDate`
    | `location.type`
    | `participants.list.${number}.name`
    | `recurrenceRule.days`
    | `recurrenceRule.frequency`
    | `recurrenceRule.until.localDate`
    | `resources.${number}._id`
    | `revision`
    | `start.localDate`,
    5
  >,
  options?: UpdateEventOptions
): Promise<
  NonNullablePaths<
    Event,
    | `status`
    | `recurrenceType`
    | `recurrenceRule.frequency`
    | `recurrenceRule.days`
    | `transparency`
    | `location.type`
    | `resources`
    | `resources.${number}.transparency`
    | `resources.${number}.permissionRole`
    | `participants.list`
    | `participants.status`
    | `conferencingDetails.type`
    | `inheritedFields`
    | `permissions`
    | `permissions.${number}.identity.wixUserId`
    | `permissions.${number}.role`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    event: { ...event, id: _id },
    participantNotification: options?.participantNotification,
    timeZone: options?.timeZone,
  });

  const reqOpts = ambassadorWixCalendarV3Event.updateEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.event!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { event: '$[1]' },
        explicitPathsToArguments: {
          'event.id': '$[0]',
          participantNotification: '$[2].participantNotification',
          timeZone: '$[2].timeZone',
        },
        singleArgumentUnchanged: false,
      },
      ['_id', 'event', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateEvent {
  /**
   * Event ID.
   * @minLength 36
   * @maxLength 250
   * @readonly
   */
  _id?: string | null;
  /**
   * ID of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction)
   * the event belongs to.
   * After creating an event, you can't assign it to a different schedule.
   * @format GUID
   * @immutable
   */
  scheduleId?: string | null;
  /**
   * ID of the external schedule the event belongs to.
   *
   * For example, if the event belongs to a Bookings staff member, identical to the ID of the [resource](https://dev.wix.com/docs/sdk/backend-modules/bookings/resources/introduction). If the schedule belongs to a Bookings service, identical to the ID of the [service](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/setup).
   * @format GUID
   * @readonly
   */
  externalScheduleId?: string | null;
  /**
   * Schedule name.
   * @readonly
   */
  scheduleName?: string | null;
  /**
   * Event type. You can set the event type but you can't update it.
   *
   * Supported values:
   * + `DEFAULT`: A standard event that's not further specified.
   * + `WORKING_HOURS`: The event adds working hours to a schedule. By default not returned in [queryEvents()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/query-events).
   *
   * Additional supported values, if Wix Bookings is installed:
   * + `APPOINTMENT`: Bookings appointment event.
   * + `CLASS`: Bookings class event.
   * + `COURSE`: Bookings course event.
   * @immutable
   */
  type?: string | null;
  /**
   * Event status.
   *
   * Supported values:
   * + `CONFIRMED`: Event is scheduled to happen or has happened.
   * + `CANCELLED`: Event has been canceled.
   *
   * Default: `CONFIRMED`
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * Event title.
   *
   * Min: 1 character
   * Max: 200 characters
   * @minLength 1
   * @maxLength 200
   */
  title?: string | null;
  /** Information about when the event starts. */
  start?: ZonedDate;
  /**
   * Information about when the event ends.
   *
   * Maximum allowed date: Year 2100, or up to 100 years from the event's start date (whichever comes first).
   */
  end?: ZonedDate;
  /**
   * Information about when the event starts adjusted to the `timeZone` of the
   * business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties)
   * or a different `timeZone` you provide in the call's request.
   * @readonly
   */
  adjustedStart?: ZonedDate;
  /**
   * Information about when the event ends adjusted to the `timeZone` of the
   * business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties)
   * or a different `timeZone` you provide in the call's request.
   * @readonly
   */
  adjustedEnd?: ZonedDate;
  /**
   * Time zone the event is associated with in
   * [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * Only regional time zones and UTC are supported.
   * For example, `America/New_York` or `UTC`.
   *
   * Default: `timeZone` of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction).
   */
  timeZone?: string | null;
  /**
   * Information about whether the event is a single occurrence or part of a
   * recurring series.
   *
   * Supported values:
   * + `NONE`: The event occurs only once and doesn't repeat.
   * + `MASTER`: Defines the recurrence pattern for a series of recurring events.
   * + `INSTANCE`: A specific occurrence of a recurring event. You can't create an event with `{"recurrenceType": "INSTANCE"}`, instead it's automatically generated based on the recurrence rule. If you update an `INSTANCE` event, `recurrenceType` automatically changes to `EXCEPTION`.
   * + `EXCEPTION`: A modified instance of a recurring event that differs from the recurrence pattern. For example, an event with a different time or location. You can't create an `EXCEPTION` event directly, instead it's set automatically when you update an `INSTANCE` event.
   *
   * Default: `NONE`
   * @readonly
   */
  recurrenceType?: RecurrenceTypeWithLiterals;
  /**
   * Recurrence pattern for a series of events. This field is required when
   * creating a `MASTER` event and isn't available for non-recurring events.
   * You can't update the recurrence rule for `INSTANCE` or `EXCEPTION` events.
   *
   * For example, an event that repeats every second Monday until January 7, 2026,
   * at 8:00 AM has the following `recurrenceRule`:
   * - `frequency = WEEKLY`
   * - `interval = 2`
   * - `days = [MONDAY]`
   * - `until = 20260107T08:00:00Z`
   */
  recurrenceRule?: RecurrenceRule;
  /**
   * ID of the `MASTER` event the event belongs to. Available only for `INSTANCE`
   * and `EXCEPTION` events.
   * @minLength 64
   * @maxLength 64
   * @readonly
   */
  recurringEventId?: string | null;
  /**
   * Specifies whether the event blocks time in the schedule it belongs to.
   *
   * Supported values:
   * + `OPAQUE`:  The schedule is blocked during the event, preventing other events that involve the same entities (for example, Booking staff members) from being scheduled at the same time.
   * + `TRANSPARENT`: The schedule remains open during the event, allowing other events to be scheduled concurrently.
   *
   * Default: `OPAQUE`
   */
  transparency?: TransparencyWithLiterals;
  /**
   * Event location.
   *
   * Default: `defaultLocation` of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction) the event belongs to
   */
  location?: Location;
  /**
   * List of [Wix Bookings resources](https://dev.wix.com/docs/sdk/backend-modules/bookings/resources/introduction)
   * affected by the event. This could include, for example, the
   * [Wix Bookings staff member](https://dev.wix.com/docs/sdk/backend-modules/bookings/staff-members/introduction)
   * providing the service or the room where the appointment takes place.
   *
   * Max: 100 resources
   * @maxSize 100
   */
  resources?: Resource[];
  /**
   * Maximum number of participants who can participate in the event.
   *
   * Default: `defaultCapacity` of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction) the event belongs to.
   */
  totalCapacity?: number | null;
  /**
   * Number of participants who can still be added to the event.
   * @readonly
   */
  remainingCapacity?: number | null;
  /**
   * Information about the event's participants.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions#retrieve-personal-information).
   * @readonly
   */
  participants?: Participants;
  /**
   * Information about the event's online conferencing.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions#retrieve-personal-information).
   *
   * Default: `defaultConferencingDetails` of the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction) the event belongs to.
   */
  conferencingDetails?: ConferencingDetails;
  /**
   * Additional notes about the event.
   * Returned only if explicitly requested. For more details, see the [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions#retrieve-personal-information).
   *
   * Min: 1 character
   * Max: 5000 characters
   * @minLength 1
   * @maxLength 5000
   */
  notes?: string | null;
  /**
   * List of fields whose values are inherited.
   * + For single-occurrence events, values are inherited from the [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction).
   * + For `INSTANCE` and `EXCEPTION` events, values are inherited from the related `MASTER` event.
   * @readonly
   */
  inheritedFields?: FieldWithLiterals[];
  /**
   * ID of the app that owns the event. Identical to `appId` of the
   * [schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction)
   * to which the event belongs.
   * @format GUID
   * @readonly
   */
  appId?: string | null;
  /**
   * List of permissions associated with the event. Refer to the
   * [permissions article](https://dev.wix.com/docs/sdk/backend-modules/calendar/permissions#roles)
   * for more information.
   * @readonly
   */
  permissions?: Permission[];
  /** Extensions enabling applications or users to save custom data related to the event. */
  extendedFields?: ExtendedFields;
  /**
   * Revision number, which increments each time the event is updated.
   * To prevent conflicting changes, the current revision must be passed when updating the event.
   * Ignored when creating an event.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date the event was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date the event was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface UpdateEventOptions {
  /**
   * Information about whether participants of the updated event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

/**
 * Updates multiple events in bulk.
 *
 *
 * When updating an event with `{"recurrenceType": "INSTANCE"}`, the
 * `recurrenceType` automatically changes to `EXCEPTION`.
 *
 * When updating an event with `{"recurrenceType": "MASTER"}`:
 * + Past `INSTANCE` and `EXCEPTION` events remain unchanged.
 * + For future `INSTANCE` events, all relevant fields are updated.
 * + For future `EXCEPTION` events, only changes to the `inheritedFields` are
 * applied, while other fields remain unchanged.
 * @param events - Events to update.
 *
 * Min: 1 event
 * Max: 50 events
 * @public
 * @requiredField events
 * @requiredField events.event
 * @requiredField events.event._id
 * @requiredField events.event.conferencingDetails.guestUrl
 * @requiredField events.event.conferencingDetails.hostUrl
 * @requiredField events.event.end.localDate
 * @requiredField events.event.location.type
 * @requiredField events.event.participants.list.name
 * @requiredField events.event.recurrenceRule.days
 * @requiredField events.event.recurrenceRule.frequency
 * @requiredField events.event.recurrenceRule.until.localDate
 * @requiredField events.event.resources._id
 * @requiredField events.event.revision
 * @requiredField events.event.start.localDate
 * @permissionId CALENDAR.EVENT_UPDATE_OWN
 * @permissionId CALENDAR.EVENT_UPDATE
 * @applicableIdentity APP
 * @fqn wix.calendar.events.v3.EventsService.BulkUpdateEvent
 */
export async function bulkUpdateEvent(
  events: NonNullablePaths<
    BulkUpdateEventRequestMaskedEvent,
    | `event`
    | `event._id`
    | `event.conferencingDetails.guestUrl`
    | `event.conferencingDetails.hostUrl`
    | `event.end.localDate`
    | `event.location.type`
    | `event.participants.list.${number}.name`
    | `event.recurrenceRule.days`
    | `event.recurrenceRule.frequency`
    | `event.recurrenceRule.until.localDate`
    | `event.resources.${number}._id`
    | `event.revision`
    | `event.start.localDate`,
    6
  >[],
  options?: BulkUpdateEventOptions
): Promise<
  NonNullablePaths<
    BulkUpdateEventResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.status`
    | `results.${number}.item.recurrenceType`
    | `results.${number}.item.recurrenceRule.frequency`
    | `results.${number}.item.transparency`
    | `results.${number}.item.location.type`
    | `results.${number}.item.participants.status`
    | `results.${number}.item.conferencingDetails.type`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    events: events,
    returnEntity: options?.returnEntity,
    participantNotification: options?.participantNotification,
    timeZone: options?.timeZone,
  });

  const reqOpts = ambassadorWixCalendarV3Event.bulkUpdateEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          events: '$[0]',
          returnEntity: '$[1].returnEntity',
          participantNotification: '$[1].participantNotification',
          timeZone: '$[1].timeZone',
        },
        singleArgumentUnchanged: false,
      },
      ['events', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateEventOptions {
  /**
   * Whether to return the updated events.
   *
   * Default: `false`
   */
  returnEntity?: boolean | null;
  /**
   * Information about whether participants of the updated event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

/**
 * Restores the event's default values from the relevant schedule or `MASTER`
 * event.
 *
 *
 * You must provide `fields` to specify which `inheritedFields` are restored.
 *
 * For example, if you've created an event that takes place in a non-default
 * location with a custom title and capacity, but you want to reset only title
 * and capacity to default values without affecting the location, provide the
 * event ID and set `fields` to `["TITLE", "CAPACITY"]`.
 *
 * Refer to the [default values article](https://dev.wix.com/docs/sdk/backend-modules/calendar/default-values)
 * for more information.
 * @param eventId - ID of the event for which to restore default values.
 * @param fields - Fields for which to restore default values.
 *
 * `TIME` restores default values for `start` and `end`.
 *
 * Min: 1 field
 * @public
 * @requiredField eventId
 * @requiredField fields
 * @permissionId CALENDAR.EVENT_UPDATE_OWN
 * @permissionId CALENDAR.EVENT_UPDATE
 * @applicableIdentity APP
 * @fqn wix.calendar.events.v3.EventsService.RestoreEventDefaults
 */
export async function restoreEventDefaults(
  eventId: string,
  fields: FieldWithLiterals[],
  options?: RestoreEventDefaultsOptions
): Promise<
  NonNullablePaths<
    RestoreEventDefaultsResponse,
    | `event.status`
    | `event.recurrenceType`
    | `event.recurrenceRule.frequency`
    | `event.recurrenceRule.days`
    | `event.transparency`
    | `event.location.type`
    | `event.resources`
    | `event.resources.${number}.transparency`
    | `event.resources.${number}.permissionRole`
    | `event.participants.list`
    | `event.participants.status`
    | `event.conferencingDetails.type`
    | `event.inheritedFields`
    | `event.permissions`
    | `event.permissions.${number}.identity.wixUserId`
    | `event.permissions.${number}.role`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    fields: fields,
    participantNotification: options?.participantNotification,
    timeZone: options?.timeZone,
  });

  const reqOpts = ambassadorWixCalendarV3Event.restoreEventDefaults(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          fields: '$[1]',
          participantNotification: '$[2].participantNotification',
          timeZone: '$[2].timeZone',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'fields', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface RestoreEventDefaultsOptions {
  /**
   * Information about whether participants of the updated event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

/**
 * Splits a recurring `MASTER` event into 2 separate `MASTER` events.
 *
 *
 * You must provide a `splitLocalDate` that's in the future and after the `start`
 * date of the next `INSTANCE` or `EXCEPTION` event in the series. Additionally,
 * there must be another `INSTANCE` or `EXCEPTION` event following this next event,
 * as the changes wouldn't affect any event without a subsequent occurrence.
 *
 * If you want to modify a `MASTER` event with the changes already applying to
 * the very next event, use
 * [updateEvent()](https://dev.wix.com/docs/sdk/backend-modules/calendar/events/update-event)
 * instead, as this preserves past events and only modifies future events.
 *
 * The main consequences are:
 * - **The original `MASTER` event is shortened**. Its `recurrenceRule.until.localDate`
 * is updated to `end.localDate` of the latest `INSTANCE` or `EXCEPTION`
 * event starting before `splitLocalDate`. If an `INSTANCE` or `EXCEPTION` event
 * starts before but ends after `splitLocalDate`, `until.localDate` is set
 * to value that's later than `splitLocalDate`.
 * - **A new `MASTER` event is created**, starting from the first event that
 * begins after `splitLocalDate`.
 * - **Existing `INSTANCE` and `EXCEPTION` events that occur after the split are updated**.
 * Their `recurringEventId` is set to the ID of the newly created `MASTER` event.
 * @param recurringEventId - ID of the `MASTER` event to split.
 * @param splitLocalDate - Local date and time at which the `MASTER` event is split in
 * [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format. For example,
 * `2025-03-11T09:00:00`.
 * Must be a future date that's after the `start` date of the next `INSTANCE` or
 * `EXCEPTION` event in the series. Additionally, there must be another
 * `INSTANCE` or `EXCEPTION` event following this next event, as the changes
 * wouldn't affect any event without a subsequent occurrence.
 * @public
 * @requiredField recurringEventId
 * @requiredField splitLocalDate
 * @permissionId CALENDAR.EVENT_UPDATE_OWN
 * @permissionId CALENDAR.EVENT_UPDATE
 * @applicableIdentity APP
 * @fqn wix.calendar.events.v3.EventsService.SplitRecurringEvent
 */
export async function splitRecurringEvent(
  recurringEventId: string,
  splitLocalDate: string,
  options?: SplitRecurringEventOptions
): Promise<
  NonNullablePaths<
    SplitRecurringEventResponse,
    | `updatedRecurringEventEndingBeforeSplit.status`
    | `updatedRecurringEventEndingBeforeSplit.recurrenceType`
    | `updatedRecurringEventEndingBeforeSplit.recurrenceRule.frequency`
    | `updatedRecurringEventEndingBeforeSplit.recurrenceRule.days`
    | `updatedRecurringEventEndingBeforeSplit.transparency`
    | `updatedRecurringEventEndingBeforeSplit.location.type`
    | `updatedRecurringEventEndingBeforeSplit.resources`
    | `updatedRecurringEventEndingBeforeSplit.resources.${number}.transparency`
    | `updatedRecurringEventEndingBeforeSplit.resources.${number}.permissionRole`
    | `updatedRecurringEventEndingBeforeSplit.participants.list`
    | `updatedRecurringEventEndingBeforeSplit.participants.status`
    | `updatedRecurringEventEndingBeforeSplit.conferencingDetails.type`
    | `updatedRecurringEventEndingBeforeSplit.inheritedFields`
    | `updatedRecurringEventEndingBeforeSplit.permissions`
    | `updatedRecurringEventEndingBeforeSplit.permissions.${number}.identity.wixUserId`
    | `updatedRecurringEventEndingBeforeSplit.permissions.${number}.role`
    | `newRecurringEventStartingFromSplit.status`
    | `newRecurringEventStartingFromSplit.recurrenceType`
    | `newRecurringEventStartingFromSplit.recurrenceRule.frequency`
    | `newRecurringEventStartingFromSplit.recurrenceRule.days`
    | `newRecurringEventStartingFromSplit.transparency`
    | `newRecurringEventStartingFromSplit.location.type`
    | `newRecurringEventStartingFromSplit.resources`
    | `newRecurringEventStartingFromSplit.resources.${number}.transparency`
    | `newRecurringEventStartingFromSplit.resources.${number}.permissionRole`
    | `newRecurringEventStartingFromSplit.participants.list`
    | `newRecurringEventStartingFromSplit.participants.status`
    | `newRecurringEventStartingFromSplit.conferencingDetails.type`
    | `newRecurringEventStartingFromSplit.inheritedFields`
    | `newRecurringEventStartingFromSplit.permissions`
    | `newRecurringEventStartingFromSplit.permissions.${number}.identity.wixUserId`
    | `newRecurringEventStartingFromSplit.permissions.${number}.role`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[3] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    recurringEventId: recurringEventId,
    splitLocalDate: splitLocalDate,
    timeZone: options?.timeZone,
  });

  const reqOpts = ambassadorWixCalendarV3Event.splitRecurringEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          recurringEventId: '$[0]',
          splitLocalDate: '$[1]',
          timeZone: '$[2].timeZone',
        },
        singleArgumentUnchanged: false,
      },
      ['recurringEventId', 'splitLocalDate', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SplitRecurringEventOptions {
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

/**
 * Cancels an event.
 * @param eventId - ID of the event to cancel.
 * @public
 * @requiredField eventId
 * @permissionId CALENDAR.EVENT_CANCEL_OWN
 * @permissionId CALENDAR.EVENT_CANCEL
 * @applicableIdentity APP
 * @fqn wix.calendar.events.v3.EventsService.CancelEvent
 */
export async function cancelEvent(
  eventId: string,
  options?: CancelEventOptions
): Promise<
  NonNullablePaths<
    CancelEventResponse,
    | `event.status`
    | `event.recurrenceType`
    | `event.recurrenceRule.frequency`
    | `event.recurrenceRule.days`
    | `event.transparency`
    | `event.location.type`
    | `event.resources`
    | `event.resources.${number}.transparency`
    | `event.resources.${number}.permissionRole`
    | `event.participants.list`
    | `event.participants.status`
    | `event.conferencingDetails.type`
    | `event.inheritedFields`
    | `event.permissions`
    | `event.permissions.${number}.identity.wixUserId`
    | `event.permissions.${number}.role`,
    6
  > & {
    __applicationErrorsType?: CancelEventApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventId: eventId,
    participantNotification: options?.participantNotification,
    timeZone: options?.timeZone,
  });

  const reqOpts = ambassadorWixCalendarV3Event.cancelEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventId: '$[0]',
          participantNotification: '$[1].participantNotification',
          timeZone: '$[1].timeZone',
        },
        singleArgumentUnchanged: false,
      },
      ['eventId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CancelEventOptions {
  /**
   * Information about whether participants of the canceled event are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}

/**
 * Cancels multiple events in bulk.
 * @param eventIds - IDs of the events to cancel.
 * @public
 * @requiredField eventIds
 * @permissionId CALENDAR.EVENT_CANCEL_OWN
 * @permissionId CALENDAR.EVENT_CANCEL
 * @applicableIdentity APP
 * @fqn wix.calendar.events.v3.EventsService.BulkCancelEvent
 */
export async function bulkCancelEvent(
  eventIds: string[],
  options?: BulkCancelEventOptions
): Promise<
  NonNullablePaths<
    BulkCancelEventResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.status`
    | `results.${number}.item.recurrenceType`
    | `results.${number}.item.recurrenceRule.frequency`
    | `results.${number}.item.transparency`
    | `results.${number}.item.location.type`
    | `results.${number}.item.participants.status`
    | `results.${number}.item.conferencingDetails.type`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`,
    6
  > & {
    __applicationErrorsType?: BulkCancelEventApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    eventIds: eventIds,
    returnEntity: options?.returnEntity,
    participantNotification: options?.participantNotification,
    timeZone: options?.timeZone,
  });

  const reqOpts = ambassadorWixCalendarV3Event.bulkCancelEvent(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          eventIds: '$[0]',
          returnEntity: '$[1].returnEntity',
          participantNotification: '$[1].participantNotification',
          timeZone: '$[1].timeZone',
        },
        singleArgumentUnchanged: false,
      },
      ['eventIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCancelEventOptions {
  /**
   * Whether to return the canceled events.
   *
   * Default: `false`
   */
  returnEntity?: boolean | null;
  /**
   * Information about whether participants of the canceled events are notified and
   * the message they receive.
   */
  participantNotification?: ParticipantNotification;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database)
   * for calculating `adjustedStart` and `adjustedEnd`. For example,
   * `America/New_York` or `UTC`.
   *
   * Default: `timeZone` specified in the business [site properties](https://dev.wix.com/docs/sdk/backend-modules/business-tools/site-properties/get-site-properties).
   */
  timeZone?: string | null;
}
